interface RgbColor {
  r: number;
  g: number;
  b: number;
}

interface HsvColor {
  h: number;
  s: number;
  v: number;
}

const DEFAULT_RGB: RgbColor = { r: 57, g: 102, b: 248 };

const hexToRgb = (color: string): RgbColor => {
  const value = String(color || '').replace('#', '');
  const normalized =
    value.length === 3
      ? value
          .split('')
          .map(character => `${character}${character}`)
          .join('')
      : value;
  const parsed = parseInt(normalized, 16);

  if (normalized.length !== 6 || Number.isNaN(parsed)) {
    return DEFAULT_RGB;
  }

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255
  };
};

const rgbToHex = ({ r, g, b }: RgbColor): string => {
  const toHex = (channel: number) =>
    Math.max(0, Math.min(255, Math.round(channel)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const rgbToHsv = ({ r, g, b }: RgbColor): HsvColor => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return {
    h,
    s: max === 0 ? 0 : delta / max,
    v: max
  };
};

export const hsvToRgb = ({ h, s, v }: HsvColor): RgbColor => {
  const chroma = v * s;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const match = v - chroma;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (h < 60) [red, green, blue] = [chroma, x, 0];
  else if (h < 120) [red, green, blue] = [x, chroma, 0];
  else if (h < 180) [red, green, blue] = [0, chroma, x];
  else if (h < 240) [red, green, blue] = [0, x, chroma];
  else if (h < 300) [red, green, blue] = [x, 0, chroma];
  else [red, green, blue] = [chroma, 0, x];

  return {
    r: Math.round((red + match) * 255),
    g: Math.round((green + match) * 255),
    b: Math.round((blue + match) * 255)
  };
};

export const hexToHsv = (color: string): HsvColor => rgbToHsv(hexToRgb(color));

export const hsvToHex = (hsv: HsvColor): string => rgbToHex(hsvToRgb(hsv));

export const isDarkColor = (color: string): boolean => {
  const { r, g, b } = hexToRgb(color);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.62;
};

export const colorWithAlpha = (color: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const mixColor = (color: string, target: 'white' | 'black', amount: number): string => {
  const source = hexToRgb(color);
  const destination = target === 'white' ? 255 : 0;
  const mix = (channel: number) => Math.round(channel + (destination - channel) * amount);
  return `rgb(${mix(source.r)}, ${mix(source.g)}, ${mix(source.b)})`;
};

/** Normalize a colour string to lowercase `#rrggbb`, or null if invalid. */
export const normalizeHexColor = (color: string): string | null => {
  const value = String(color || '')
    .trim()
    .replace(/^#/, '');
  const normalized =
    value.length === 3
      ? value
          .split('')
          .map(character => `${character}${character}`)
          .join('')
      : value;
  if (normalized.length !== 6 || Number.isNaN(parseInt(normalized, 16))) {
    return null;
  }
  return `#${normalized.toLowerCase()}`;
};

export type { RgbColor, HsvColor };
export { hexToRgb, rgbToHex };
